/**
 * ExitIntentOffer — one-shot recovery modal shown when a first-time
 * visitor moves their mouse to the top of the viewport (desktop) or
 * scrolls back up fast (mobile). Offers "$2.50 first month" on the
 * DIY tier via a ?coupon=WELCOME50 param the signup page reads.
 *
 * Fires at most ONCE per browser (localStorage flag). Never fires for
 * signed-in users or people already past /signup. Adds a hidden coupon
 * to the /signup CTA so we can attribute conversions.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, ArrowRight } from "lucide-react";

const STORAGE_KEY = "crc_exit_intent_shown_v1";

export default function ExitIntentOffer() {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return undefined;
      if (localStorage.getItem("crc_session_token")) return undefined;
      const path = window.location.pathname;
      if (path.startsWith("/signup") || path.startsWith("/login") ||
          path.startsWith("/dashboard") || path.startsWith("/checkout")) return undefined;
    } catch { /* localStorage blocked — just proceed */ }

    const onMouseLeave = (e) => {
      if (dismissed || open) return;
      if (e.clientY <= 0) trigger();
    };
    let lastY = window.scrollY;
    const onScroll = () => {
      if (dismissed || open) return;
      const y = window.scrollY;
      if (lastY - y > 60 && y < 400) trigger();
      lastY = y;
    };
    const trigger = () => {
      setOpen(true);
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
    };

    document.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("scroll", onScroll);
    };
  }, [dismissed, open]);

  if (!open) return null;

  const close = () => { setDismissed(true); setOpen(false); };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      data-testid="exit-intent-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 animate-in fade-in duration-200"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{
        background: "radial-gradient(1200px 400px at 50% 0%, rgba(16,185,129,0.22) 0%, transparent 60%), rgba(2,6,23,0.72)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
    >
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          background: "linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)",
          boxShadow: "0 40px 100px -20px rgba(2,6,23,0.6), 0 0 0 1px rgba(148,163,184,0.14)",
        }}
      >
        {/* accent bar */}
        <div
          aria-hidden
          style={{
            height: "4px",
            background: "linear-gradient(90deg,#10b981 0%,#059669 50%,#0ea5e9 100%)",
          }}
        />

        <button
          type="button"
          onClick={close}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors"
          data-testid="exit-intent-close"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4"/>
        </button>

        <div className="p-8 sm:p-10 text-center">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-extrabold tracking-[0.22em] uppercase"
            style={{ background: "rgba(16,185,129,0.10)", color: "#065f46", border: "1px solid rgba(16,185,129,0.24)" }}
          >
            <Sparkles className="w-3.5 h-3.5"/> One-time offer
          </div>
          <h2
            id="exit-intent-title"
            className="font-display mt-4 font-extrabold text-slate-900 leading-tight"
            style={{ fontSize: "clamp(1.6rem, 3.2vw, 2rem)" }}
          >
            Wait — 50% off your first month.
          </h2>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            Try the full Central Rentals platform for <b className="text-emerald-700">CA$2.50</b> your first month on the DIY plan. Cancel anytime from your Billing page.
          </p>

          <div
            className="mt-7 rounded-2xl px-6 py-5 flex items-baseline justify-center gap-3"
            style={{
              background: "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(14,165,233,0.08) 100%)",
              border: "1px solid rgba(16,185,129,0.18)",
            }}
          >
            <span className="text-slate-400 line-through text-lg tabular-nums">CA$5</span>
            <span className="font-display text-5xl font-extrabold tabular-nums" style={{ color: "#059669" }}>CA$2.50</span>
            <span className="text-xs text-slate-500 self-end pb-2">/ first month</span>
          </div>

          <Link
            to="/signup?plan=diy&coupon=WELCOME50"
            onClick={close}
            data-testid="exit-intent-claim"
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "14px 20px",
              borderRadius: "9999px",
              background: "linear-gradient(135deg,#10b981 0%,#059669 100%)",
              color: "#ffffff",
              fontWeight: 800,
              fontSize: "16px",
              textDecoration: "none",
              marginTop: "24px",
              boxShadow: "0 10px 30px -8px rgba(16,185,129,0.5)",
            }}
          >
            Claim my 50% off <ArrowRight className="w-5 h-5"/>
          </Link>
          <button
            type="button"
            onClick={close}
            className="mt-3 text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
            data-testid="exit-intent-decline"
          >
            No thanks, I&apos;ll pay full price later
          </button>
        </div>
        <div className="px-8 py-3 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500 text-center">
          Coupon <code className="font-mono font-bold text-slate-700">WELCOME50</code> · Applies to DIY tier · 1-time use
        </div>
      </div>
    </div>
  );
}

